import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";
import { toast } from "sonner";

interface UpdateItemData {
  id: string;
  name?: string;
  description?: string;
  price_per_hour?: number;
}

export const useUpdateReservationItem = () => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfileQuery();

  return useMutation({
    mutationFn: async (data: UpdateItemData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !profile) throw new Error("Usuario no autenticado");

      // Verify manager has access to the item's gym
      const { data: item } = await supabase
        .from('reservation_items')
        .select(`
          category_id,
          reservation_categories (
            gym_id
          )
        `)
        .eq('id', data.id)
        .single();

      if (!item) throw new Error("Elemento no encontrado");

      const gymId = (item.reservation_categories as any)?.gym_id;
      const { data: gymManager } = await supabase
        .from('gym_managers')
        .select('*')
        .eq('manager_id', user.id)
        .eq('gym_id', gymId)
        .single();

      if (!gymManager && profile.role !== 'admin') {
        throw new Error("No tienes permisos para gestionar este gimnasio");
      }

      const { data: updatedItem, error } = await supabase
        .from('reservation_items')
        .update({
          ...(data.name && { name: data.name }),
          ...(data.description !== undefined && { description: data.description }),
          ...(data.price_per_hour && { price_per_hour: data.price_per_hour }),
          updated_at: new Date().toISOString()
        })
        .eq('id', data.id)
        .select()
        .single();

      if (error) throw error;
      return updatedItem;
    },
    onSuccess: (item, variables) => {
      // Get categoryId from the original data to invalidate the right cache
      queryClient.invalidateQueries({ 
        queryKey: ['reservation-items', (item as any).category_id] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['reservation-items'] 
      });
      toast.success("Elemento actualizado exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al actualizar el elemento");
    },
  });
};